import { useQueryClient, type QueryKey } from "@tanstack/react-query";
import { Pencil, Plus, Trash2, Zap } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type * as S from "@shipyard/shared";
import { FEATURES, LOGO_MAX_BYTES, LOGO_TYPES, ORG_ROLES, TAG_COLORS } from "@shipyard/shared/constants";
import { ConfirmDialog, CopyButton, Mono, SecretInput, toastError } from "@/components/common";
import { Guide } from "@/components/guides";
import { TagBadge, TagFields } from "@/components/tags";
import { Badge, Button, Card, CardHeader, Dialog, EmptyState, ErrorBox, Field, Input, PageHeader, Select, Spinner, Switch, Table, Td, Textarea, Th } from "@/components/ui";
import { http } from "@/lib/api";
import {
  keys,
  useAppleCredentials,
  useApps,
  useDevices,
  useEntitlements,
  useGitProviders,
  useGoogleCredentials,
  useLlmProviders,
  useMe,
  useOrgBranding,
  useOrgInvitations,
  useOrgMembers,
  useSettings,
  useSsoProviders,
  useSwitchOrg,
  useTags,
  useUsers,
} from "@/lib/queries";
import { isInstanceAdmin } from "@/lib/roles";
import { cn, formatDate } from "@/lib/utils";

// ------------------------------------------------------------------ helpers

function TestButton({ path, body }: { path: string; body?: unknown }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  return (
    <Button
      variant="ghost"
      size="sm"
      loading={busy}
      onClick={async () => {
        setBusy(true);
        try {
          const res = await http.post<S.CredentialTestResult>(path, body ?? {});
          const lines = res.checks?.length ? res.checks.map((c) => `${c.ok ? "✓" : "✗"} ${c.label} — ${c.message}`) : [res.message];
          const apps = res.apps?.slice(0, 8).map((a) => `  • ${a.name ?? a.id}`) ?? [];
          const description = <div className="whitespace-pre-line text-xs">{[...lines, ...apps].join("\n")}</div>;
          if (res.ok) toast.success(t("admin.testOk"), { description, duration: 10_000 });
          else toast.error(t("admin.testFailed"), { description, duration: 20_000 });
        } catch (err) {
          toastError(err);
        } finally {
          setBusy(false);
        }
      }}
    >
      {!busy && <Zap className="h-3.5 w-3.5" />}
      {t("common.test")}
    </Button>
  );
}

function DeleteButton({ path, name, queryKey, onDeleted }: { path: string; name: string; queryKey: QueryKey; onDeleted?: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return (
    <ConfirmDialog
      danger
      title={`${t("common.delete")} « ${name} »`}
      description={t("common.irreversible")}
      confirmLabel={t("common.delete")}
      onConfirm={async () => {
        await http.del(path);
        toast.success(t("common.deleted"));
        void qc.invalidateQueries({ queryKey });
        onDeleted?.();
      }}
      trigger={(open) => (
        <Button variant="ghost" size="icon" onClick={open} aria-label={t("common.delete")}>
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
    />
  );
}

/** Generic create/edit dialog for admin resources. */
function EditDialog<T extends Record<string, unknown>>({
  title,
  initial,
  render,
  onSubmit,
  trigger,
}: {
  title: string;
  initial: T;
  render: (value: T, set: (patch: Partial<T>) => void) => ReactNode;
  onSubmit: (value: T) => Promise<unknown>;
  trigger: (open: () => void) => ReactNode;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState<T>(initial);
  const [busy, setBusy] = useState(false);
  return (
    <>
      {trigger(() => {
        setValue(initial);
        setOpen(true);
      })}
      <Dialog
        open={open}
        onOpenChange={setOpen}
        title={title}
        wide
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              loading={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await onSubmit(value);
                  toast.success(t("common.saved"));
                  setOpen(false);
                } catch (err) {
                  toastError(err);
                } finally {
                  setBusy(false);
                }
              }}
            >
              {t("common.save")}
            </Button>
          </>
        }
      >
        <div className="space-y-4">{render(value, (patch) => setValue((v) => ({ ...v, ...patch })))}</div>
      </Dialog>
    </>
  );
}

function ListCard({ title, description, action, loading, empty, children }: { title: ReactNode; description?: ReactNode; action: ReactNode; loading: boolean; empty: boolean; children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader title={title} description={description} actions={action} />
      {loading ? (
        <div className="p-5">
          <Spinner />
        </div>
      ) : empty ? (
        <div className="p-5">
          <EmptyState title={t("common.none")} />
        </div>
      ) : (
        children
      )}
    </Card>
  );
}

// ------------------------------------------------------------------ Organization

const ORG_ROLE_LABELS: Record<S.OrgRole, string> = { owner: "org.roleOwner", admin: "org.roleAdmin", member: "org.roleMember" };

export function OrganizationPage() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const me = useMe();
  const members = useOrgMembers();
  const switchOrg = useSwitchOrg();
  const entitlements = useEntitlements();
  const org = me.data?.organization;
  const [shareLink, setShareLink] = useState<S.Invitation | null>(null);
  const canCreateOrg =
    entitlements.data?.deployment === "cloud" || (isInstanceAdmin(me.data) && !!entitlements.data?.features.includes("multi_org"));
  if (!org) return <Spinner />;
  const isOwner = org.role === "owner";

  const setRole = async (userId: string, role: S.OrgRole) => {
    try {
      qc.setQueryData(keys.orgMembers, await http.patch<S.OrgMember[]>(`/org/members/${userId}`, { role }));
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <>
      <PageHeader
        title={org.name}
        description={t("org.help")}
        actions={
          <>
            <EditDialog
              title={t("org.rename")}
              initial={{ name: org.name }}
              onSubmit={async (v) => {
                await http.patch("/org", v);
                await qc.invalidateQueries({ queryKey: keys.me });
                await qc.invalidateQueries({ queryKey: keys.orgs });
              }}
              render={(v, set) => (
                <Field label={t("common.name")} htmlFor="org-name">
                  <Input id="org-name" value={v.name} onChange={(e) => set({ name: e.target.value })} />
                </Field>
              )}
              trigger={(open) => (
                <Button variant="outline" onClick={open}>
                  <Pencil className="h-4 w-4" />
                  {t("org.rename")}
                </Button>
              )}
            />
            {isInstanceAdmin(me.data) && !canCreateOrg && (
              <Button disabled title={t("license.requiresEnterprise")}>
                <Plus className="h-4 w-4" />
                {t("org.new")}
                <Badge tone="violet">Enterprise</Badge>
              </Button>
            )}
            {canCreateOrg && (
              <EditDialog
                title={t("org.new")}
                initial={{ name: "" }}
                onSubmit={async (v) => {
                  const created = await http.post<S.Organization>("/orgs", v);
                  await switchOrg.mutateAsync(created.id);
                }}
                render={(v, set) => (
                  <Field label={t("common.name")} help={t("org.newHelp")} htmlFor="new-org-name">
                    <Input id="new-org-name" value={v.name} onChange={(e) => set({ name: e.target.value })} />
                  </Field>
                )}
                trigger={(open) => (
                  <Button onClick={open}>
                    <Plus className="h-4 w-4" />
                    {t("org.new")}
                  </Button>
                )}
              />
            )}
          </>
        }
      />
      <ListCard
        title={t("org.members")}
        description={t("org.membersHelp")}
        loading={members.isLoading}
        empty={!members.data?.length}
        action={
          <EditDialog
            title={t("org.invite")}
            initial={{ email: "", role: "member" as S.OrgRole }}
            onSubmit={async (v) => {
              const invitation = await http.post<S.InvitationResult>("/org/invitations", v);
              await qc.invalidateQueries({ queryKey: keys.orgInvitations });
              if (invitation.emailSent) toast.success(t("org.invitationSent", { email: invitation.email }));
              else setShareLink(invitation);
            }}
            render={(v, set) => (
              <>
                <Field label={t("common.email")} help={t("org.inviteHelp")} htmlFor="m-email">
                  <Input id="m-email" type="email" value={v.email} onChange={(e) => set({ email: e.target.value })} />
                </Field>
                <Field label={t("common.role")} htmlFor="m-role">
                  <Select id="m-role" value={v.role} onChange={(e) => set({ role: e.target.value as S.OrgRole })}>
                    {ORG_ROLES.filter((r) => r !== "owner" || isOwner).map((r) => (
                      <option key={r} value={r}>
                        {t(ORG_ROLE_LABELS[r])}
                      </option>
                    ))}
                  </Select>
                </Field>
              </>
            )}
            trigger={(open) => (
              <Button size="sm" onClick={open}>
                <Plus className="h-3.5 w-3.5" />
                {t("org.invite")}
              </Button>
            )}
          />
        }
      >
        <Table>
          <thead>
            <tr>
              <Th>{t("common.name")}</Th>
              <Th>{t("common.role")}</Th>
              <Th className="hidden md:table-cell">{t("org.memberSince")}</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {members.data?.map((m) => {
              const self = m.userId === me.data?.id;
              const locked = self || (m.role === "owner" && !isOwner);
              return (
                <tr key={m.userId}>
                  <Td>
                    <p className="font-medium">
                      {m.name} {self && <span className="text-xs font-normal text-fg-muted">({t("admin.you")})</span>}
                    </p>
                    <p className="text-xs text-fg-muted">{m.email}</p>
                  </Td>
                  <Td>
                    <Select className="h-8 w-40 text-xs" value={m.role} disabled={locked} onChange={(e) => setRole(m.userId, e.target.value as S.OrgRole)}>
                      {ORG_ROLES.filter((r) => r !== "owner" || isOwner || m.role === "owner").map((r) => (
                        <option key={r} value={r}>
                          {t(ORG_ROLE_LABELS[r])}
                        </option>
                      ))}
                    </Select>
                  </Td>
                  <Td className="hidden text-fg-muted md:table-cell">{formatDate(m.createdAt, i18n.language)}</Td>
                  <Td className="text-right">
                    {!locked && <DeleteButton path={`/org/members/${m.userId}`} name={m.name} queryKey={keys.orgMembers} />}
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </ListCard>
      <InvitationsCard />
      <Dialog open={!!shareLink} onOpenChange={(open) => !open && setShareLink(null)} title={t("org.shareLinkTitle")}>
        {shareLink && (
          <div className="space-y-3">
            <p className="text-sm text-fg-muted">{t("org.shareLinkHelp", { email: shareLink.email })}</p>
            <div className="flex items-center gap-2">
              <Input readOnly value={shareLink.url} aria-label={t("org.invitationLink")} onFocus={(e) => e.currentTarget.select()} />
              <CopyButton value={shareLink.url} />
            </div>
          </div>
        )}
      </Dialog>
      <div className="mt-6">
        <SsoCard />
      </div>
      <div className="mt-6">
        <BrandingCard />
      </div>
    </>
  );
}

// ------------------------------------------------------------------ Invitations

function InvitationsCard() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const invitations = useOrgInvitations();
  const resend = async (id: string) => {
    try {
      const invitation = await http.post<S.InvitationResult>(`/org/invitations/${id}/resend`);
      await qc.invalidateQueries({ queryKey: keys.orgInvitations });
      toast.success(invitation.emailSent ? t("org.invitationSent", { email: invitation.email }) : t("org.invitationExtended"));
    } catch (err) {
      toastError(err);
    }
  };
  if (!invitations.data?.length) return null;
  return (
    <Card className="mt-6">
      <CardHeader title={t("org.pendingInvitations")} description={t("org.pendingInvitationsHelp")} />
      <Table>
        <thead>
          <tr>
            <Th>{t("common.email")}</Th>
            <Th>{t("common.role")}</Th>
            <Th className="hidden md:table-cell">{t("org.invitationExpires")}</Th>
            <Th />
          </tr>
        </thead>
        <tbody>
          {invitations.data.map((inv) => (
            <tr key={inv.id}>
              <Td>
                <p className="font-medium">{inv.email}</p>
                {inv.inviter && <p className="text-xs text-fg-muted">{t("org.invitedBy", { name: inv.inviter.name })}</p>}
              </Td>
              <Td>{t(ORG_ROLE_LABELS[inv.role])}</Td>
              <Td className="hidden text-fg-muted md:table-cell">
                {inv.status === "expired" ? <Badge tone="amber">{t("org.invitationExpired")}</Badge> : formatDate(inv.expiresAt, i18n.language)}
              </Td>
              <Td className="text-right">
                <div className="flex items-center justify-end gap-1">
                  <CopyButton value={inv.url} label={t("org.copyLink")} />
                  <Button variant="ghost" size="sm" onClick={() => void resend(inv.id)}>
                    {t("org.resend")}
                  </Button>
                  <DeleteButton path={`/org/invitations/${inv.id}`} name={inv.email} queryKey={keys.orgInvitations} />
                </div>
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
}

// ------------------------------------------------------------------ White label

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1] ?? "");
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function BrandingCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const entitlements = useEntitlements();
  const enabled = !!entitlements.data?.features.includes("white_label");
  const branding = useOrgBranding();
  const [form, setForm] = useState<S.UpdateOrgBrandingBody | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (branding.data) {
      const { logoUrl: _logo, ...rest } = branding.data;
      setForm(rest);
    }
  }, [branding.data]);

  if (entitlements.data && !enabled) {
    return (
      <Card>
        <CardHeader
          title={
            <span className="flex items-center gap-2">
              {t("branding.title")} <Badge tone="violet">Enterprise</Badge>
            </span>
          }
          description={t("branding.requiresEnterprise")}
        />
      </Card>
    );
  }
  if (!form || !branding.data) return <Spinner />;
  const set = (patch: Partial<S.UpdateOrgBrandingBody>) => setForm({ ...form, ...patch });
  const text = (v: string) => (v.trim() ? v : null);

  return (
    <Card>
      <CardHeader title={t("branding.title")} description={t("branding.help")} />
      <div className="grid gap-4 p-5 sm:grid-cols-2">
        <Field label={t("branding.displayName")} htmlFor="brand-name">
          <Input id="brand-name" placeholder="Acme" value={form.displayName ?? ""} onChange={(e) => set({ displayName: text(e.target.value) })} />
        </Field>
        <Field label={t("branding.color")} htmlFor="brand-color">
          <div className="flex gap-2">
            <input
              type="color"
              aria-label={t("branding.colorPicker")}
              className="h-9 w-12 cursor-pointer rounded border bg-panel"
              value={form.primaryColor ?? "#0284c7"}
              onChange={(e) => set({ primaryColor: e.target.value })}
            />
            <Input id="brand-color" placeholder="#0284c7" value={form.primaryColor ?? ""} onChange={(e) => set({ primaryColor: text(e.target.value) })} />
          </div>
        </Field>
        <Field label={t("branding.supportUrl")} htmlFor="brand-url">
          <Input id="brand-url" placeholder="https://help.acme.com" value={form.supportUrl ?? ""} onChange={(e) => set({ supportUrl: text(e.target.value) })} />
        </Field>
        <Field label={t("branding.supportEmail")} htmlFor="brand-email">
          <Input id="brand-email" type="email" placeholder="qa@acme.com" value={form.supportEmail ?? ""} onChange={(e) => set({ supportEmail: text(e.target.value) })} />
        </Field>
        <Field label={t("branding.logo")} help={t("branding.logoHelp")} htmlFor="brand-logo">
          <div className="flex items-center gap-3">
            {branding.data.logoUrl && <img src={branding.data.logoUrl} alt="" className="h-9 max-w-32 rounded border bg-white object-contain p-1" />}
            <input
              id="brand-logo"
              type="file"
              accept={LOGO_TYPES.join(",")}
              className="text-xs"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (!file) return;
                try {
                  if (file.size > LOGO_MAX_BYTES) throw new Error(t("branding.logoTooLarge"));
                  const res = await http.put<S.OrgBranding>("/org/branding/logo", { contentType: file.type, data: await readAsBase64(file) });
                  qc.setQueryData(keys.orgBranding, res);
                } catch (err) {
                  toastError(err);
                }
              }}
            />
            {branding.data.logoUrl && (
              <Button variant="ghost" size="sm" onClick={async () => qc.setQueryData(keys.orgBranding, await http.del<S.OrgBranding>("/org/branding/logo"))}>
                {t("branding.removeLogo")}
              </Button>
            )}
          </div>
        </Field>
        <label className="flex items-center gap-3 self-end text-sm">
          <Switch aria-label={t("branding.hidePoweredBy")} checked={form.hidePoweredBy} onCheckedChange={(hidePoweredBy) => set({ hidePoweredBy })} />
          {t("branding.hidePoweredBy")}
        </label>
      </div>
      <div className="flex justify-end border-t px-5 py-3">
        <Button
          loading={busy}
          onClick={async () => {
            setBusy(true);
            try {
              qc.setQueryData(keys.orgBranding, await http.put<S.OrgBranding>("/org/branding", form));
              toast.success(t("common.saved"));
            } catch (err) {
              toastError(err);
            } finally {
              setBusy(false);
            }
          }}
        >
          {t("common.save")}
        </Button>
      </div>
    </Card>
  );
}

// ------------------------------------------------------------------ Single sign-on

type NewProvider = { type: "oidc" | "saml"; domain: string; issuer: string; clientId: string; clientSecret: string; entryPoint: string; cert: string };

function DomainVerification({ provider }: { provider: S.SsoProvider }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [record, setRecord] = useState<S.SsoDomainVerification | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={async () => {
          setError(null);
          try {
            setRecord(await http.post<S.SsoDomainVerification>(`/org/sso-providers/${provider.id}/domain-verification`));
          } catch (err) {
            toastError(err);
          }
        }}
      >
        {t("sso.verifyDomain")}
      </Button>
      <Dialog
        open={!!record}
        onOpenChange={(o) => !o && setRecord(null)}
        title={t("sso.verifyTitle", { domain: provider.domain })}
        description={t("sso.verifyHelp")}
        wide
        footer={
          <Button
            loading={busy}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await http.post(`/org/sso-providers/${provider.id}/verify-domain`);
                toast.success(t("sso.verified"));
                setRecord(null);
                void qc.invalidateQueries({ queryKey: keys.ssoProviders });
                void qc.invalidateQueries({ queryKey: keys.status });
              } catch (err) {
                setError(err);
              } finally {
                setBusy(false);
              }
            }}
          >
            {t("sso.check")}
          </Button>
        }
      >
        {record && (
          <div className="space-y-3 text-sm">
            <Field label={t("sso.recordName")}>
              <div className="flex gap-2">
                <Input readOnly aria-label={t("sso.recordName")} value={record.recordName} className="font-mono text-xs" />
                <CopyButton value={record.recordName} />
              </div>
            </Field>
            <Field label={t("sso.recordValue")}>
              <div className="flex gap-2">
                <Input readOnly aria-label={t("sso.recordValue")} value={record.recordValue} className="font-mono text-xs" />
                <CopyButton value={record.recordValue} />
              </div>
            </Field>
            <ErrorBox error={error} />
          </div>
        )}
      </Dialog>
    </>
  );
}

function SsoCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const entitlements = useEntitlements();
  const enabled = !!entitlements.data?.features.includes("sso");
  const providers = useSsoProviders(enabled);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: keys.ssoProviders });
    void qc.invalidateQueries({ queryKey: keys.status });
  };

  if (entitlements.data && !enabled) {
    return (
      <Card>
        <CardHeader
          title={
            <span className="flex items-center gap-2">
              {t("sso.title")} <Badge tone="violet">Enterprise</Badge>
            </span>
          }
          description={t("sso.requiresEnterprise")}
        />
      </Card>
    );
  }

  return (
    <ListCard
      title={t("sso.title")}
      description={t("sso.help")}
      loading={providers.isLoading}
      empty={!providers.data?.length}
      action={
        <EditDialog<NewProvider>
          title={t("sso.add")}
          initial={{ type: "oidc", domain: "", issuer: "", clientId: "", clientSecret: "", entryPoint: "", cert: "" }}
          onSubmit={async (v) => {
            const body =
              v.type === "oidc"
                ? { type: v.type, domain: v.domain, issuer: v.issuer, clientId: v.clientId, clientSecret: v.clientSecret }
                : { type: v.type, domain: v.domain, issuer: v.issuer, entryPoint: v.entryPoint, cert: v.cert };
            await http.post("/org/sso-providers", body);
            refresh();
          }}
          render={(v, set) => (
            <>
              <Field label={t("sso.protocol")} htmlFor="sso-type">
                <Select id="sso-type" value={v.type} onChange={(e) => set({ type: e.target.value as NewProvider["type"] })}>
                  <option value="oidc">OpenID Connect</option>
                  <option value="saml">SAML 2.0</option>
                </Select>
              </Field>
              <Field label={t("sso.domain")} help={t("sso.domainHelp")} htmlFor="sso-domain">
                <Input id="sso-domain" placeholder="acme.com" value={v.domain} onChange={(e) => set({ domain: e.target.value })} />
              </Field>
              {v.type === "oidc" ? (
                <>
                  <Field label={t("sso.issuer")} help={t("sso.issuerHelp")} htmlFor="sso-issuer">
                    <Input id="sso-issuer" placeholder="https://login.acme.com" value={v.issuer} onChange={(e) => set({ issuer: e.target.value })} />
                  </Field>
                  <Field label={t("sso.clientId")} htmlFor="sso-client-id">
                    <Input id="sso-client-id" value={v.clientId} onChange={(e) => set({ clientId: e.target.value })} />
                  </Field>
                  <Field label={t("sso.clientSecret")} htmlFor="sso-client-secret">
                    <Input id="sso-client-secret" type="password" value={v.clientSecret} onChange={(e) => set({ clientSecret: e.target.value })} />
                  </Field>
                </>
              ) : (
                <>
                  <Field label={t("sso.entityId")} htmlFor="sso-entity">
                    <Input id="sso-entity" value={v.issuer} onChange={(e) => set({ issuer: e.target.value })} />
                  </Field>
                  <Field label={t("sso.entryPoint")} htmlFor="sso-entry">
                    <Input id="sso-entry" placeholder="https://idp.acme.com/sso" value={v.entryPoint} onChange={(e) => set({ entryPoint: e.target.value })} />
                  </Field>
                  <Field label={t("sso.cert")} htmlFor="sso-cert">
                    <Textarea id="sso-cert" rows={4} className="font-mono text-xs" placeholder="-----BEGIN CERTIFICATE-----" value={v.cert} onChange={(e) => set({ cert: e.target.value })} />
                  </Field>
                </>
              )}
            </>
          )}
          trigger={(open) => (
            <Button size="sm" onClick={open}>
              <Plus className="h-3.5 w-3.5" />
              {t("sso.add")}
            </Button>
          )}
        />
      }
    >
      <ul className="divide-y">
        {providers.data?.map((p) => (
          <li key={p.id} className="space-y-3 px-5 py-4">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone="blue">{p.type === "oidc" ? "OIDC" : "SAML"}</Badge>
              <span className="font-medium">{p.domain}</span>
              {p.domainVerified ? <Badge tone="green">{t("sso.domainVerified")}</Badge> : <Badge tone="amber">{t("sso.domainNotVerified")}</Badge>}
              <span className="truncate text-xs text-fg-muted">{p.issuer}</span>
              <div className="ml-auto flex items-center gap-2">
                {!p.domainVerified && <DomainVerification provider={p} />}
                <DeleteButton path={`/org/sso-providers/${p.id}`} name={p.domain} queryKey={keys.ssoProviders} onDeleted={refresh} />
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="text-fg-muted">{t("sso.callbackUrl")}</span>
              <Mono>{p.callbackUrl}</Mono>
              <CopyButton value={p.callbackUrl} />
              {p.samlMetadataUrl && (
                <a className="text-brand-600 hover:underline" href={p.samlMetadataUrl} target="_blank" rel="noreferrer">
                  {t("sso.metadata")}
                </a>
              )}
            </div>
            <label className="flex items-center gap-3 text-sm">
              <Switch
                aria-label={t("sso.enforce")}
                checked={p.enforced}
                disabled={!p.domainVerified}
                onCheckedChange={async (enforced) => {
                  try {
                    await http.patch(`/org/sso-providers/${p.id}`, { enforced });
                    refresh();
                  } catch (err) {
                    toastError(err);
                  }
                }}
              />
              <span>
                <span className="font-medium">{t("sso.enforce")}</span>
                <span className="block text-xs text-fg-muted">{t("sso.enforceHelp")}</span>
              </span>
            </label>
          </li>
        ))}
      </ul>
    </ListCard>
  );
}

// ------------------------------------------------------------------ License

/** License dates are days (UTC end of day): no time, no time zone shift. */
export const licenseDate = (iso: string, lang: string) => new Date(iso).toLocaleDateString(lang, { timeZone: "UTC", dateStyle: "long" });

const LICENSE_TONES: Record<S.LicenseStatus, "green" | "amber" | "red"> = { valid: "green", grace: "amber", expired: "red", invalid: "red" };

export function LicensePage() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const entitlements = useEntitlements();
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const data = entitlements.data;
  if (!data) return <Spinner />;
  const license = data.license;

  const save = async (request: () => Promise<S.Entitlements>) => {
    setBusy(true);
    try {
      qc.setQueryData(keys.entitlements, await request());
      setKey("");
      toast.success(t("common.saved"));
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader title={t("license.title")} description={t("license.help")} />
      <div className="space-y-6">
        <Card>
          <CardHeader
            title={
              <span className="flex items-center gap-2">
                {t(`license.plan.${data.plan}`)}
                {license && <Badge tone={LICENSE_TONES[license.status]}>{t(`license.status.${license.status}`)}</Badge>}
              </span>
            }
            description={license?.error ?? (license?.customer ? t("license.licensedTo", { customer: license.customer }) : t("license.community"))}
          />
          <dl className="grid gap-4 p-5 text-sm sm:grid-cols-3">
            <div>
              <dt className="text-fg-muted">{t("license.seats")}</dt>
              <dd className={cn("font-medium", data.seats.limit !== null && data.seats.used > data.seats.limit && "text-red-600")}>
                {data.seats.limit === null ? t("license.seatsUnlimited", { used: data.seats.used }) : t("license.seatsUsed", { used: data.seats.used, limit: data.seats.limit })}
              </dd>
            </div>
            {license?.expiresAt && (
              <div>
                <dt className="text-fg-muted">{t("license.expiresAt")}</dt>
                <dd className="font-medium">{licenseDate(license.expiresAt, i18n.language)}</dd>
              </div>
            )}
            <div>
              <dt className="text-fg-muted">{t("license.features")}</dt>
              <dd className="flex flex-wrap gap-1 pt-0.5">
                {FEATURES.map((f) => (
                  <Badge key={f} tone={data.features.includes(f) ? "blue" : "neutral"} className={data.features.includes(f) ? undefined : "opacity-50"}>
                    {t(`license.feature.${f}`)}
                  </Badge>
                ))}
              </dd>
            </div>
          </dl>
        </Card>
        {license?.fromEnv ? (
          <p className="text-sm text-fg-muted">{t("license.fromEnv")}</p>
        ) : (
          <Card>
            <CardHeader title={license ? t("license.replace") : t("license.install")} description={t("license.installHelp")} />
            <div className="space-y-3 p-5">
              <Textarea aria-label={t("license.key")} rows={4} className="font-mono text-xs" value={key} onChange={(e) => setKey(e.target.value)} placeholder="shp_lic1.…" />
              <div className="flex gap-2">
                <Button disabled={!key.trim()} loading={busy} onClick={() => save(() => http.put<S.Entitlements>("/admin/license", { key: key.trim() }))}>
                  {t("license.install")}
                </Button>
                {license && (
                  <Button variant="ghost" disabled={busy} onClick={() => save(() => http.del<S.Entitlements>("/admin/license"))}>
                    {t("license.remove")}
                  </Button>
                )}
              </div>
            </div>
          </Card>
        )}
      </div>
    </>
  );
}

// ------------------------------------------------------------------ Users

export function AdminUsersPage() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const me = useMe();
  const users = useUsers();
  const settings = useSettings();
  const refresh = () => qc.invalidateQueries({ queryKey: keys.users });

  const update = async (id: string, body: Partial<{ role: S.UserRole; disabled: boolean; password: string; name: string }>) => {
    try {
      await http.patch(`/admin/users/${id}`, body);
      await refresh();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <>
      <PageHeader title={t("admin.users")} />
      <div className="space-y-6">
        <Card>
          <div className="flex items-center justify-between gap-4 px-5 py-4">
            <div>
              <p className="font-medium">{t("admin.signup")}</p>
              <p className="text-sm text-fg-muted">{t("admin.signupHelp")}</p>
            </div>
            <Switch
              checked={!!settings.data?.signupEnabled}
              disabled={!settings.data}
              onCheckedChange={async (signupEnabled) => {
                try {
                  await http.patch("/admin/settings", { signupEnabled });
                  void qc.invalidateQueries({ queryKey: keys.settings });
                  void qc.invalidateQueries({ queryKey: keys.status });
                } catch (err) {
                  toastError(err);
                }
              }}
            />
          </div>
        </Card>
        <ListCard
          title={t("admin.users")}
          loading={users.isLoading}
          empty={!users.data?.length}
          action={
            <EditDialog
              title={t("admin.newUser")}
              initial={{ name: "", email: "", password: "", role: "user" as S.UserRole }}
              onSubmit={async (v) => {
                await http.post("/admin/users", v);
                await refresh();
              }}
              render={(v, set) => (
                <>
                  <Field label={t("common.name")} htmlFor="u-name">
                    <Input id="u-name" value={v.name} onChange={(e) => set({ name: e.target.value })} />
                  </Field>
                  <Field label={t("common.email")} htmlFor="u-email">
                    <Input id="u-email" type="email" value={v.email} onChange={(e) => set({ email: e.target.value })} />
                  </Field>
                  <Field label={t("common.password")} help={t("auth.passwordHint")} htmlFor="u-pass">
                    <Input id="u-pass" type="password" value={v.password} onChange={(e) => set({ password: e.target.value })} autoComplete="new-password" />
                  </Field>
                  <Field label={t("common.role")} htmlFor="u-role">
                    <Select id="u-role" value={v.role} onChange={(e) => set({ role: e.target.value as S.UserRole })}>
                      <option value="user">{t("admin.user")}</option>
                      <option value="admin">{t("admin.admin")}</option>
                    </Select>
                  </Field>
                </>
              )}
              trigger={(open) => (
                <Button size="sm" onClick={open}>
                  <Plus className="h-3.5 w-3.5" />
                  {t("admin.newUser")}
                </Button>
              )}
            />
          }
        >
          <Table>
            <thead>
              <tr>
                <Th>{t("common.name")}</Th>
                <Th>{t("common.role")}</Th>
                <Th className="hidden md:table-cell">{t("common.createdAt")}</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {users.data?.map((u) => {
                const self = u.id === me.data?.id;
                return (
                  <tr key={u.id} className={u.disabled ? "opacity-60" : undefined}>
                    <Td>
                      <p className="font-medium">
                        {u.name} {self && <span className="text-xs font-normal text-fg-muted">({t("admin.you")})</span>}
                      </p>
                      <p className="text-xs text-fg-muted">{u.email}</p>
                    </Td>
                    <Td>
                      <div className="flex items-center gap-2">
                        <Select className="h-8 w-40 text-xs" value={u.role} disabled={self} onChange={(e) => update(u.id, { role: e.target.value as S.UserRole })}>
                          <option value="user">{t("admin.user")}</option>
                          <option value="admin">{t("admin.admin")}</option>
                        </Select>
                        {u.disabled && <Badge tone="red">{t("admin.disabled")}</Badge>}
                      </div>
                    </Td>
                    <Td className="hidden text-fg-muted md:table-cell">{formatDate(u.createdAt, i18n.language)}</Td>
                    <Td className="text-right">
                      {!self && (
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => update(u.id, { disabled: !u.disabled })}>
                            {u.disabled ? t("admin.enable") : t("admin.disable")}
                          </Button>
                          <EditDialog
                            title={`${t("admin.resetPassword")} — ${u.name}`}
                            initial={{ password: "" }}
                            onSubmit={(v) => http.patch(`/admin/users/${u.id}`, { password: v.password })}
                            render={(v, set) => (
                              <Field label={t("admin.newPassword")} help={t("auth.passwordHint")} htmlFor="reset-pass">
                                <Input id="reset-pass" type="password" value={v.password} onChange={(e) => set({ password: e.target.value })} autoComplete="new-password" />
                              </Field>
                            )}
                            trigger={(open) => (
                              <Button variant="ghost" size="icon" onClick={open} aria-label={t("admin.resetPassword")}>
                                <Pencil className="h-4 w-4" />
                              </Button>
                            )}
                          />
                          <DeleteButton path={`/admin/users/${u.id}`} name={u.name} queryKey={keys.users} />
                        </div>
                      )}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </ListCard>
      </div>
    </>
  );
}

// ------------------------------------------------------------------ Store credentials

export function AdminCredentialsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const apple = useAppleCredentials();
  const google = useGoogleCredentials();

  const appleForm = (v: { name: string; keyId: string; issuerId: string; keyType: "team" | "individual"; privateKey: string }, set: (p: Partial<typeof v>) => void, editing: boolean) => (
    <>
      <Guide kind="apple" defaultOpen={!editing} />
      <Field label={t("common.name")} htmlFor="a-name">
        <Input id="a-name" value={v.name} onChange={(e) => set({ name: e.target.value })} placeholder="Acme Inc." />
      </Field>
      <div className="grid gap-4 sm:grid-cols-3">
        <Field label={t("admin.keyType")} htmlFor="a-type">
          <Select id="a-type" value={v.keyType} onChange={(e) => set({ keyType: e.target.value as "team" | "individual" })}>
            <option value="team">{t("admin.keyTeam")}</option>
            <option value="individual">{t("admin.keyIndividual")}</option>
          </Select>
        </Field>
        <Field label={t("admin.keyId")} htmlFor="a-kid">
          <Input id="a-kid" value={v.keyId} onChange={(e) => set({ keyId: e.target.value })} className="font-mono" />
        </Field>
        <Field label={t("admin.issuerId")} htmlFor="a-iss">
          <Input id="a-iss" value={v.issuerId} disabled={v.keyType === "individual"} onChange={(e) => set({ issuerId: e.target.value })} className="font-mono" />
        </Field>
      </div>
      <Field label={t("admin.p8")} help={editing ? t("admin.replaceSecret") : t("admin.p8Help")}>
        <SecretInput value={v.privateKey} onChange={(privateKey) => set({ privateKey })} accept=".p8" placeholder="-----BEGIN PRIVATE KEY-----" />
      </Field>
    </>
  );

  const appleBody = (v: { name: string; keyId: string; issuerId: string; keyType: "team" | "individual"; privateKey: string }) => ({
    name: v.name,
    keyId: v.keyId,
    keyType: v.keyType,
    issuerId: v.keyType === "team" ? v.issuerId || null : null,
    ...(v.privateKey ? { privateKey: v.privateKey } : {}),
  });

  const googleForm = (v: { name: string; serviceAccountJson: string }, set: (p: Partial<typeof v>) => void, editing: boolean) => (
    <>
      <Guide kind="google" defaultOpen={!editing} />
      <Field label={t("common.name")} htmlFor="g-name">
        <Input id="g-name" value={v.name} onChange={(e) => set({ name: e.target.value })} placeholder="Play Console" />
      </Field>
      <Field label={t("admin.serviceAccount")} help={editing ? t("admin.replaceSecret") : t("admin.serviceAccountHelp")}>
        <SecretInput value={v.serviceAccountJson} onChange={(serviceAccountJson) => set({ serviceAccountJson })} accept=".json,application/json" rows={6} placeholder='{ "type": "service_account", … }' />
      </Field>
    </>
  );

  return (
    <>
      <PageHeader title={t("nav.credentials")} />
      <div className="space-y-6">
        <ListCard
          title={t("admin.apple")}
          description={t("admin.appleHelp")}
          loading={apple.isLoading}
          empty={!apple.data?.length}
          action={
            <EditDialog
              title={t("admin.apple")}
              initial={{ name: "", keyId: "", issuerId: "", keyType: "team" as "team" | "individual", privateKey: "" }}
              onSubmit={async (v) => {
                await http.post("/admin/apple-credentials", appleBody(v));
                void qc.invalidateQueries({ queryKey: keys.apple });
              }}
              render={(v, set) => appleForm(v, set, false)}
              trigger={(open) => (
                <Button size="sm" onClick={open}>
                  <Plus className="h-3.5 w-3.5" />
                  {t("common.add")}
                </Button>
              )}
            />
          }
        >
          <Table>
            <thead>
              <tr>
                <Th>{t("common.name")}</Th>
                <Th>{t("admin.keyId")}</Th>
                <Th className="hidden md:table-cell">{t("admin.issuerId")}</Th>
                <Th className="hidden lg:table-cell">{t("devices.certificate")}</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {apple.data?.map((c) => (
                <tr key={c.id}>
                  <Td className="font-medium">{c.name}</Td>
                  <Td>
                    <Mono>{c.keyId}</Mono>
                  </Td>
                  <Td className="hidden font-mono text-xs text-fg-muted md:table-cell">{c.issuerId ?? t("admin.keyIndividual")}</Td>
                  <Td className="hidden lg:table-cell">
                    <DistributionCertificate credential={c} />
                  </Td>
                  <Td className="text-right">
                    <div className="flex justify-end gap-1">
                      <TestButton path={`/admin/apple-credentials/${c.id}/test`} />
                      <EditDialog
                        title={c.name}
                        initial={{ name: c.name, keyId: c.keyId, issuerId: c.issuerId ?? "", keyType: c.keyType, privateKey: "" }}
                        onSubmit={async (v) => {
                          await http.patch(`/admin/apple-credentials/${c.id}`, appleBody(v));
                          void qc.invalidateQueries({ queryKey: keys.apple });
                        }}
                        render={(v, set) => appleForm(v, set, true)}
                        trigger={(open) => (
                          <Button variant="ghost" size="icon" onClick={open} aria-label={t("common.edit")}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                      />
                      <DeleteButton path={`/admin/apple-credentials/${c.id}`} name={c.name} queryKey={keys.apple} />
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </ListCard>

        <ListCard
          title={t("admin.google")}
          description={t("admin.googleHelp")}
          loading={google.isLoading}
          empty={!google.data?.length}
          action={
            <EditDialog
              title={t("admin.google")}
              initial={{ name: "", serviceAccountJson: "" }}
              onSubmit={async (v) => {
                await http.post("/admin/google-credentials", v);
                void qc.invalidateQueries({ queryKey: keys.google });
              }}
              render={(v, set) => googleForm(v, set, false)}
              trigger={(open) => (
                <Button size="sm" onClick={open}>
                  <Plus className="h-3.5 w-3.5" />
                  {t("common.add")}
                </Button>
              )}
            />
          }
        >
          <Table>
            <thead>
              <tr>
                <Th>{t("common.name")}</Th>
                <Th>Service account</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {google.data?.map((c) => (
                <tr key={c.id}>
                  <Td className="font-medium">{c.name}</Td>
                  <Td className="font-mono text-xs text-fg-muted">{c.clientEmail}</Td>
                  <Td className="text-right">
                    <div className="flex justify-end gap-1">
                      <TestButton path={`/admin/google-credentials/${c.id}/test`} />
                      <EditDialog
                        title={c.name}
                        initial={{ name: c.name, serviceAccountJson: "" }}
                        onSubmit={async (v) => {
                          await http.patch(`/admin/google-credentials/${c.id}`, { name: v.name, ...(v.serviceAccountJson ? { serviceAccountJson: v.serviceAccountJson } : {}) });
                          void qc.invalidateQueries({ queryKey: keys.google });
                        }}
                        render={(v, set) => googleForm(v, set, true)}
                        trigger={(open) => (
                          <Button variant="ghost" size="icon" onClick={open} aria-label={t("common.edit")}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                      />
                      <DeleteButton path={`/admin/google-credentials/${c.id}`} name={c.name} queryKey={keys.google} />
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </ListCard>
      </div>
    </>
  );
}

// ------------------------------------------------------------------ LLM providers

type LlmForm = { name: string; baseUrl: string; apiKey: string; model: string; temperature: number };

export function AdminLlmPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const providers = useLlmProviders();
  const form = (v: LlmForm, set: (p: Partial<LlmForm>) => void, editing: boolean) => (
    <>
      <Field label={t("common.name")} htmlFor="l-name">
        <Input id="l-name" value={v.name} onChange={(e) => set({ name: e.target.value })} placeholder="OpenRouter" />
      </Field>
      <Field label={t("admin.baseUrl")} htmlFor="l-url">
        <Input id="l-url" value={v.baseUrl} onChange={(e) => set({ baseUrl: e.target.value })} className="font-mono" />
      </Field>
      <div className="grid gap-4 sm:grid-cols-[1fr_8rem]">
        <Field label={t("admin.model")} htmlFor="l-model">
          <Input id="l-model" value={v.model} onChange={(e) => set({ model: e.target.value })} placeholder="anthropic/claude-sonnet-5" className="font-mono" />
        </Field>
        <Field label={t("admin.temperature")} htmlFor="l-temp">
          <Input id="l-temp" type="number" step={0.1} min={0} max={2} value={v.temperature} onChange={(e) => set({ temperature: Number(e.target.value) })} />
        </Field>
      </div>
      <Field label={t("admin.apiKey")} help={editing ? t("admin.replaceSecret") : undefined} htmlFor="l-key">
        <Input id="l-key" type="password" value={v.apiKey} onChange={(e) => set({ apiKey: e.target.value })} autoComplete="off" />
      </Field>
    </>
  );
  const body = (v: LlmForm) => ({ name: v.name, baseUrl: v.baseUrl, model: v.model, temperature: v.temperature, ...(v.apiKey ? { apiKey: v.apiKey } : {}) });
  return (
    <>
      <PageHeader title={t("admin.llmTitle")} description={t("admin.llmHelp")} />
      <ListCard
        title={t("admin.llmTitle")}
        loading={providers.isLoading}
        empty={!providers.data?.length}
        action={
          <EditDialog
            title={t("admin.llmTitle")}
            initial={{ name: "", baseUrl: "https://openrouter.ai/api/v1", apiKey: "", model: "", temperature: 0.3 }}
            onSubmit={async (v) => {
              await http.post("/admin/llm-providers", body(v));
              void qc.invalidateQueries({ queryKey: keys.llm });
            }}
            render={(v, set) => form(v, set, false)}
            trigger={(open) => (
              <Button size="sm" onClick={open}>
                <Plus className="h-3.5 w-3.5" />
                {t("common.add")}
              </Button>
            )}
          />
        }
      >
        <Table>
          <thead>
            <tr>
              <Th>{t("common.name")}</Th>
              <Th>{t("admin.model")}</Th>
              <Th className="hidden md:table-cell">{t("admin.baseUrl")}</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {providers.data?.map((p) => (
              <tr key={p.id}>
                <Td className="font-medium">{p.name}</Td>
                <Td>
                  <Mono>{p.model}</Mono>
                </Td>
                <Td className="hidden font-mono text-xs text-fg-muted md:table-cell">{p.baseUrl}</Td>
                <Td className="text-right">
                  <div className="flex justify-end gap-1">
                    <TestButton path={`/admin/llm-providers/${p.id}/test`} />
                    <EditDialog
                      title={p.name}
                      initial={{ name: p.name, baseUrl: p.baseUrl, apiKey: "", model: p.model, temperature: p.temperature }}
                      onSubmit={async (v) => {
                        await http.patch(`/admin/llm-providers/${p.id}`, body(v));
                        void qc.invalidateQueries({ queryKey: keys.llm });
                      }}
                      render={(v, set) => form(v, set, true)}
                      trigger={(open) => (
                        <Button variant="ghost" size="icon" onClick={open} aria-label={t("common.edit")}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                    />
                    <DeleteButton path={`/admin/llm-providers/${p.id}`} name={p.name} queryKey={keys.llm} />
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </ListCard>
    </>
  );
}

// ------------------------------------------------------------------ Git providers

const GIT_TYPE_LABELS: Record<S.GitProviderType, string> = {
  github: "GitHub",
  gitlab: "GitLab",
  gitea: "Gitea / Forgejo",
  bitbucket: "Bitbucket",
  azure_devops: "Azure DevOps",
};

type GitForm = { name: string; type: S.GitProviderType; baseUrl: string; token: string };

export function AdminGitPage() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const providers = useGitProviders();
  const form = (v: GitForm, set: (p: Partial<GitForm>) => void, editing: boolean) => (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t("common.name")} htmlFor="gp-name">
          <Input id="gp-name" value={v.name} onChange={(e) => set({ name: e.target.value })} placeholder="GitHub" />
        </Field>
        <Field label={t("admin.type")} htmlFor="gp-type">
          <Select id="gp-type" value={v.type} onChange={(e) => set({ type: e.target.value as S.GitProviderType })}>
            {Object.entries(GIT_TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <Field label={t("admin.baseUrl")} help={t(v.type === "azure_devops" ? "admin.baseUrlHelpAzure" : "admin.baseUrlHelp")} htmlFor="gp-url">
        <Input
          id="gp-url"
          value={v.baseUrl}
          onChange={(e) => set({ baseUrl: e.target.value })}
          className="font-mono"
          placeholder={v.type === "azure_devops" ? "https://dev.azure.com/my-org" : undefined}
        />
      </Field>
      <Field label={t("admin.token")} help={editing ? t("admin.replaceSecret") : t("admin.tokenHelp")} htmlFor="gp-token">
        <Input id="gp-token" type="password" value={v.token} onChange={(e) => set({ token: e.target.value })} autoComplete="off" />
      </Field>
    </>
  );
  const body = (v: GitForm) => ({ name: v.name, type: v.type, baseUrl: v.baseUrl.trim() || null, ...(v.token ? { token: v.token } : {}) });
  return (
    <>
      <PageHeader title={t("admin.gitTitle")} description={t("admin.gitHelp")} />
      <ListCard
        title={t("admin.gitTitle")}
        loading={providers.isLoading}
        empty={!providers.data?.length}
        action={
          <EditDialog
            title={t("admin.gitTitle")}
            initial={{ name: "", type: "github" as S.GitProviderType, baseUrl: "", token: "" }}
            onSubmit={async (v) => {
              await http.post("/admin/git-providers", body(v));
              void qc.invalidateQueries({ queryKey: keys.git });
            }}
            render={(v, set) => form(v, set, false)}
            trigger={(open) => (
              <Button size="sm" onClick={open}>
                <Plus className="h-3.5 w-3.5" />
                {t("common.add")}
              </Button>
            )}
          />
        }
      >
        <Table>
          <thead>
            <tr>
              <Th>{t("common.name")}</Th>
              <Th>{t("admin.type")}</Th>
              <Th className="hidden md:table-cell">{t("admin.baseUrl")}</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {providers.data?.map((p) => (
              <tr key={p.id}>
                <Td className="font-medium">
                  {p.name}
                  <p className="text-xs font-normal text-fg-muted">{formatDate(p.createdAt, i18n.language)}</p>
                </Td>
                <Td>
                  <Badge>{GIT_TYPE_LABELS[p.type]}</Badge>
                </Td>
                <Td className="hidden font-mono text-xs text-fg-muted md:table-cell">{p.baseUrl ?? "—"}</Td>
                <Td className="text-right">
                  <div className="flex justify-end gap-1">
                    <TestButton path={`/admin/git-providers/${p.id}/test`} body={{}} />
                    <EditDialog
                      title={p.name}
                      initial={{ name: p.name, type: p.type, baseUrl: p.baseUrl ?? "", token: "" }}
                      onSubmit={async (v) => {
                        await http.patch(`/admin/git-providers/${p.id}`, body(v));
                        void qc.invalidateQueries({ queryKey: keys.git });
                      }}
                      render={(v, set) => form(v, set, true)}
                      trigger={(open) => (
                        <Button variant="ghost" size="icon" onClick={open} aria-label={t("common.edit")}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                    />
                    <DeleteButton path={`/admin/git-providers/${p.id}`} name={p.name} queryKey={keys.git} />
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </ListCard>
    </>
  );
}

// ------------------------------------------------------------------ Tags

type TagForm = { name: string; color: string };

export function AdminTagsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const tags = useTags();
  const apps = useApps();
  const counts = new Map<string, number>();
  for (const app of apps.data ?? []) for (const tag of app.tags) counts.set(tag.id, (counts.get(tag.id) ?? 0) + 1);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: keys.tags });
    void qc.invalidateQueries({ queryKey: keys.apps });
  };
  const body = (v: TagForm) => ({ name: v.name.trim(), color: v.color });
  return (
    <>
      <PageHeader title={t("tags.title")} description={t("tags.help")} />
      <ListCard
        title={t("tags.title")}
        loading={tags.isLoading}
        empty={!tags.data?.length}
        action={
          <EditDialog
            title={t("tags.new")}
            initial={{ name: "", color: TAG_COLORS[0] as string }}
            onSubmit={async (v) => {
              await http.post("/tags", body(v));
              refresh();
            }}
            render={(v, set) => <TagFields value={v} onChange={set} />}
            trigger={(open) => (
              <Button size="sm" onClick={open}>
                <Plus className="h-3.5 w-3.5" />
                {t("common.add")}
              </Button>
            )}
          />
        }
      >
        <Table>
          <thead>
            <tr>
              <Th>{t("tags.tag")}</Th>
              <Th className="hidden sm:table-cell">{t("tags.color")}</Th>
              <Th>{t("tags.apps")}</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {tags.data?.map((tag) => (
              <tr key={tag.id}>
                <Td>
                  <TagBadge tag={tag} />
                </Td>
                <Td className="hidden font-mono text-xs text-fg-muted sm:table-cell">{tag.color}</Td>
                <Td className="text-fg-muted">{counts.get(tag.id) ?? 0}</Td>
                <Td className="text-right">
                  <div className="flex justify-end gap-1">
                    <EditDialog
                      title={tag.name}
                      initial={{ name: tag.name, color: tag.color }}
                      onSubmit={async (v) => {
                        await http.patch(`/tags/${tag.id}`, body(v));
                        refresh();
                      }}
                      render={(v, set) => <TagFields value={v} onChange={set} />}
                      trigger={(open) => (
                        <Button variant="ghost" size="icon" onClick={open} aria-label={t("common.edit")}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                    />
                    <DeleteButton path={`/tags/${tag.id}`} name={tag.name} queryKey={keys.tags} onDeleted={refresh} />
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </ListCard>
    </>
  );
}

// ------------------------------------------------------------------ Ad hoc distribution

function DistributionCertificate({ credential }: { credential: S.AppleCredential }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const cert = credential.distributionCertificate;
  const path = `/admin/apple-credentials/${credential.id}/distribution-certificate`;
  const refresh = () => qc.invalidateQueries({ queryKey: keys.apple });
  return (
    <div className="flex items-center gap-2">
      {cert ? (
        <span className="min-w-0 text-xs">
          <span className="block truncate font-medium" title={cert.name}>
            {cert.name}
          </span>
          <span className="text-fg-muted">{t("devices.certExpires", { date: formatDate(cert.expiresAt, i18n.language) })}</span>
        </span>
      ) : (
        <span className="text-xs text-fg-muted">{t("devices.noCertificate")}</span>
      )}
      <EditDialog
        title={t("devices.certificateTitle")}
        initial={{ p12: "", fileName: "", password: "" }}
        onSubmit={async (v) => {
          await http.put(path, { p12: v.p12, password: v.password });
          await refresh();
        }}
        render={(v, set) => (
          <>
            <p className="text-sm text-fg-muted">{t("devices.certificateHelp")}</p>
            <Field label={t("devices.p12")} htmlFor="p12-file">
              <Input
                id="p12-file"
                type="file"
                accept=".p12,.pfx"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const bytes = new Uint8Array(await file.arrayBuffer());
                  let binary = "";
                  for (const b of bytes) binary += String.fromCharCode(b);
                  set({ p12: btoa(binary), fileName: file.name });
                }}
              />
            </Field>
            <Field label={t("common.password")} htmlFor="p12-password">
              <Input id="p12-password" type="password" value={v.password} onChange={(e) => set({ password: e.target.value })} autoComplete="off" />
            </Field>
          </>
        )}
        trigger={(open) => (
          <Button variant="ghost" size="sm" onClick={open}>
            {cert ? t("devices.replace") : t("devices.addCertificate")}
          </Button>
        )}
      />
      {cert && <DeleteButton path={path} name={cert.name} queryKey={keys.apple} />}
    </div>
  );
}

const DEVICE_TONE: Record<S.DeviceStatus, "amber" | "green" | "red"> = { pending: "amber", approved: "green", rejected: "red" };

export function AdminDevicesPage() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const devices = useDevices();
  const refresh = () => qc.invalidateQueries({ queryKey: keys.devices });
  const setStatus = async (id: string, status: S.DeviceStatus) => {
    try {
      await http.patch(`/admin/devices/${id}`, { status });
      await refresh();
    } catch (err) {
      toastError(err);
    }
  };
  return (
    <>
      <PageHeader title={t("devices.title")} description={t("devices.help")} />
      <ListCard title={t("devices.title")} loading={devices.isLoading} empty={!devices.data?.length} action={null}>
        <Table>
          <thead>
            <tr>
              <Th>{t("common.name")}</Th>
              <Th className="hidden md:table-cell">UDID</Th>
              <Th>{t("devices.status")}</Th>
              <Th className="hidden lg:table-cell">{t("devices.registeredFrom")}</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {devices.data?.map((d) => (
              <tr key={d.id}>
                <Td>
                  <span className="font-medium">{d.name}</span>
                  <p className="text-xs text-fg-muted">
                    {[d.model, d.osVersion && `iOS ${d.osVersion}`].filter(Boolean).join(" · ")} · {formatDate(d.createdAt, i18n.language)}
                  </p>
                </Td>
                <Td className="hidden md:table-cell">
                  <Mono>{d.udid}</Mono>
                </Td>
                <Td>
                  <Badge tone={DEVICE_TONE[d.status]}>{t(`devices.status_${d.status}`)}</Badge>
                  {d.registeredWith.length > 0 && <p className="mt-1 text-xs text-fg-muted">{t("devices.registeredWith", { names: d.registeredWith.map((r) => r.name).join(", ") })}</p>}
                </Td>
                <Td className="hidden text-fg-muted lg:table-cell">{d.app?.name ?? "—"}</Td>
                <Td className="text-right">
                  <div className="flex justify-end gap-1">
                    {d.status !== "approved" && (
                      <Button size="sm" onClick={() => setStatus(d.id, "approved")}>
                        {t("devices.approve")}
                      </Button>
                    )}
                    {d.status !== "rejected" && (
                      <Button size="sm" variant="ghost" onClick={() => setStatus(d.id, "rejected")}>
                        {t("devices.reject")}
                      </Button>
                    )}
                    <EditDialog
                      title={d.name}
                      initial={{ name: d.name }}
                      onSubmit={async (v) => {
                        await http.patch(`/admin/devices/${d.id}`, { name: v.name });
                        await refresh();
                      }}
                      render={(v, set) => (
                        <Field label={t("common.name")} htmlFor="device-name">
                          <Input id="device-name" value={v.name} maxLength={50} onChange={(e) => set({ name: e.target.value })} />
                        </Field>
                      )}
                      trigger={(open) => (
                        <Button variant="ghost" size="icon" onClick={open} aria-label={t("common.edit")}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                    />
                    <DeleteButton path={`/admin/devices/${d.id}`} name={d.name} queryKey={keys.devices} />
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </ListCard>
    </>
  );
}
